"use client";

import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { HomeTestimonial, TestimonialSection } from "@/lib/navigation";
import { getAvatarGradient, getInitials } from "@/lib/testimonial-avatar";

import "swiper/css";
import "swiper/css/pagination";

const DEFAULT_SUB_TITLE = "Testimonial";
const DEFAULT_TITLE = "What Client Say About us";

interface TestimonialSliderProps {
  testimonials: HomeTestimonial[];
  /** CMS header copy; each field falls back to the static copy when empty. */
  section?: TestimonialSection;
}

/** Divisible by every breakpoint's slidesPerGroup, and ≥ the 3 + 3 a loop needs. */
const SLIDE_MULTIPLE = 6;

/** Repeat items up to a multiple of `multiple`, so every loop page is full. */
function fillPages<T>(items: T[], multiple: number): T[] {
  const total = Math.ceil(items.length / multiple) * multiple;
  return Array.from({ length: total }, (_, i) => items[i % items.length]);
}

export default function TestimonialSlider({
  testimonials,
  section,
}: TestimonialSliderProps) {
  const subTitle = section?.subTitle || DEFAULT_SUB_TITLE;
  const title = section?.title || DEFAULT_TITLE;

  if (!testimonials || testimonials.length === 0) return null;

  const slides = fillPages(testimonials, SLIDE_MULTIPLE);

  return (
    <section className="testi-area overflow-hidden space" id="testi-sec">
      <div className="container-fluid p-0">
        <div className="title-area mb-20 text-center">
          <span className="sub-title text-anime-style-2">{subTitle}</span>
          <h2 className="sec-title text-anime-style-3">{title}</h2>
        </div>
        <div className="slider-area">
          <Swiper
            className="th-slider testiSlider1 has-shadow"
            modules={[Pagination]}
            pagination={{
              el: ".testi-slider-pagination",
              clickable: true,
            }}
            loop={true}
            centerInsufficientSlides={true}
            breakpoints={{
              0: { slidesPerView: 1, slidesPerGroup: 1 },
              767: { slidesPerView: 2, slidesPerGroup: 2 },
              992: { slidesPerView: 2, slidesPerGroup: 2 },
              1200: { slidesPerView: 2.5, slidesPerGroup: 2 },
              1400: { slidesPerView: 3, slidesPerGroup: 3 },
            }}
          >
            {slides.map((testi, i) => (
              <SwiperSlide key={`${testi.id}-${i}`}>
                <div className="testi-card">
                  <div className="testi-card_wrapper">
                    <div className="testi-card_profile">
                      <div className="testi-card_avater">
                        {testi.photoURL ? (
                          <Image
                            src={testi.photoURL}
                            alt={testi.name}
                            width={72}
                            height={72}
                            unoptimized
                          />
                        ) : (
                          <span
                            className="testi-card_monogram"
                            aria-hidden="true"
                            style={{ background: getAvatarGradient(testi.name) }}
                          >
                            {getInitials(testi.name)}
                          </span>
                        )}
                      </div>
                      <div className="media-body">
                        <h3 className="box-title">{testi.name}</h3>
                        <span className="testi-card_desig">
                          <i className="fa-solid fa-circle-check"></i> Verified
                          Traveler
                        </span>
                      </div>
                    </div>
                    <div className="testi-card_review">
                      <i className="fa-solid fa-star"></i>
                      <i className="fa-solid fa-star"></i>
                      <i className="fa-solid fa-star"></i>
                      <i className="fa-solid fa-star"></i>
                      <i className="fa-solid fa-star"></i>
                    </div>
                  </div>
                  <div
                    className="testi-card_text mx-auto text-center"
                    dangerouslySetInnerHTML={{ __html: testi.comment }}
                  />
                  <div className="testi-card-quote shadow-sm">
                    <Image
                      src="/main/img/icon/testi-quote.svg"
                      alt=""
                      width={40}
                      height={40}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
            <div className="slider-pagination testi-slider-pagination"></div>
          </Swiper>
        </div>
      </div>
    </section>
  );
}
